import { useState } from "react";
import { ShieldCheck, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminAccount() {
  const { user, refresh } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Enter your current password to confirm changes");
      return;
    }
    if (!newEmail && !newPassword) {
      toast.error("Enter a new email and/or a new password");
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword && newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const payload = { current_password: currentPassword };
      if (newEmail) payload.new_email = newEmail;
      if (newPassword) payload.new_password = newPassword;
      const { data } = await api.patch("/auth/account", payload);
      if (data.token) localStorage.setItem("access_token", data.token);
      toast.success("Account updated successfully");
      setCurrentPassword("");
      setNewEmail("");
      setNewPassword("");
      setConfirmPassword("");
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-account-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Security</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Account Settings</h1>
        <p className="text-zinc-500 mt-1">Update your admin login email and password.</p>
      </div>

      <div className="max-w-xl bg-white border border-zinc-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-5 text-sm text-zinc-600">
          <ShieldCheck className="w-4 h-4 text-green-600" />
          Signed in as <span className="font-medium text-zinc-900" data-testid="current-admin-email">{user?.email}</span>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="admin-account-form">
          <div className="grid gap-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">New email (optional)</Label>
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder={user?.email || "new@email.com"}
              data-testid="new-email-input"
            />
          </div>
          <div className="grid gap-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">New password (optional)</Label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Min 6 characters"
              data-testid="new-password-input"
            />
          </div>
          <div className="grid gap-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Confirm new password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              data-testid="confirm-password-input"
            />
          </div>

          <div className="pt-2 border-t border-zinc-100">
            <div className="grid gap-2 mt-4">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Current password (required to confirm)</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Your current password"
                required
                data-testid="current-password-input"
              />
            </div>
          </div>

          <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" disabled={loading} data-testid="submit-account-update">
            <Save className="w-4 h-4 mr-2" />
            {loading ? "Saving…" : "Save changes"}
          </Button>
        </form>
      </div>
    </div>
  );
}
